import React from 'react'
import { format } from 'date-fns'
import { Heading, Stack, Image, Text, Link, Button, HStack } from '@chakra-ui/react'
import { LINK_COLOR } from 'constants/colors'

interface PropertyDisplayProps {
  primary_tenant_name: string
  tenants?: {
    user_id: string
    name: string
    role: string
  }[]
  address: string
  city: string
  state: string
  zip: string
  beds: number
  baths: number
  sqft: number | string | null
  portfolio_home_id: string
  photo_url?: string
  rental_id: string
  most_recent_note?: string
  most_recent_note_created_at?: Date
}

const PropertyDisplay: React.FC<React.PropsWithChildren<PropertyDisplayProps>> = (props) => {
  const {
    primary_tenant_name: tenantName,
    tenants = [],
    address,
    city,
    state,
    zip,
    beds,
    baths,
    sqft,
    portfolio_home_id: portfolioHomeId,
    photo_url: photoUrl,
    rental_id: rentalId,
    most_recent_note: mostRecentNote,
    most_recent_note_created_at: mostRecentNoteCreatedAt
  } = props

  const mostRecentNoteDate = mostRecentNoteCreatedAt
    ? format(new Date(mostRecentNoteCreatedAt), 'yyyy-MM-dd')
    : undefined
  const tenantList = tenants.length
    ? tenants
    : [
        {
          user_id: '',
          name: tenantName,
          role: 'primary'
        }
      ]
  const formatTenantRole = (role: string) => (role === 'primary' ? 'Primary' : 'Tenant')

  return (
    <Stack w='fit-content' maxW='md'>
      {photoUrl && (
        <Image key={photoUrl} src={photoUrl} w='full' alt='Property Image' borderRadius='5px' />
      )}
      <Heading as='h5' size='md' width='100%'>
        {address}
      </Heading>
      <Heading fontWeight='medium' size='sm' width='100%'>
        {city}, {state} {zip}
      </Heading>
      <Text>
        {beds} beds | {baths} baths {sqft && `| ${sqft} sqft`}
      </Text>
      <Stack spacing={0} alignItems='flex-start'>
        <Text fontWeight='semibold'>Tenants:</Text>
        {tenantList.map((tenant) =>
          tenant.user_id ? (
            <Link
              key={tenant.user_id}
              isExternal
              href={`/users/${tenant.user_id}`}
              w='fit-content'
              display='inline-block'
            >
              {tenant.name} ({formatTenantRole(tenant.role)})
            </Link>
          ) : (
            <Text key={tenant.name}>
              {tenant.name} ({formatTenantRole(tenant.role)})
            </Text>
          )
        )}
      </Stack>
      <HStack justifyContent='center'>
        <Link isExternal href={`/rent-roll/${rentalId}`}>
          <Button color={LINK_COLOR} variant='ghost'>
            Rent Roll
          </Button>
        </Link>
        <Link isExternal href={`/home/${portfolioHomeId}`}>
          <Button color={LINK_COLOR} variant='ghost'>
            Home Data
          </Button>
        </Link>
      </HStack>
      <Text>
        <span className='font-semibold'>
          Most Recent Note{' ['}
          {mostRecentNoteDate}
          {']'}
        </span>
        {': '}
        {mostRecentNote}
      </Text>
    </Stack>
  )
}

export default PropertyDisplay
